import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, Loader2, CalendarDays, Clock, XCircle } from "lucide-react";

const MONTHS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const BRL = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function MyBookings() {
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState(null);
  const [cancelling, setCancelling] = useState(null);

  async function lookup() {
    if (phone.replace(/\D/g, "").length < 8) {
      toast.error("Informe um telefone válido");
      return;
    }
    setLoading(true);
    try {
      const r = await api.get(`/bookings?phone=${encodeURIComponent(phone)}`);
      setBookings(r.data);
    } catch {
      toast.error("Erro ao buscar agendamentos");
    } finally {
      setLoading(false);
    }
  }

  async function cancel(id) {
    setCancelling(id);
    try {
      await api.delete(`/bookings/${id}`);
      setBookings((prev) =>
        prev.map((b) => (b.id === id ? { ...b, status: "cancelado" } : b))
      );
      toast.success("Agendamento cancelado");
    } catch {
      toast.error("Não foi possível cancelar");
    } finally {
      setCancelling(null);
    }
  }

  const fmt = (iso) => {
    const [y, m, d] = iso.split("-").map(Number);
    return `${d} de ${MONTHS[m - 1]} de ${y}`;
  };

  return (
    <section
      data-testid="my-bookings-section"
      className="mx-auto max-w-md px-4 py-14 md:max-w-2xl lg:max-w-4xl"
    >
      <span className="eyebrow">Meus Horários</span>
      <h2 className="font-display mt-2 text-4xl text-amber-100 sm:text-5xl">
        Consulte seu agendamento
      </h2>
      <p className="mt-3 text-sm text-zinc-400">
        Digite o WhatsApp usado no agendamento para ver ou cancelar seus horários.
      </p>

      <div className="mt-6 flex gap-2">
        <input
          data-testid="my-bookings-input"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && lookup()}
          placeholder="(11) 99999-9999"
          inputMode="tel"
          className="flex-1 rounded-xl border border-zinc-800 bg-[#161618] px-4 py-3 text-sm text-zinc-100 placeholder-zinc-600 outline-none transition-colors focus:border-amber-500/60"
        />
        <button
          data-testid="lookup-bookings-button"
          onClick={lookup}
          disabled={loading}
          className="flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-xs font-bold uppercase tracking-wider text-zinc-950 transition-all hover:bg-amber-400 active:scale-95 disabled:opacity-40"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Buscar
        </button>
      </div>

      {bookings && (
        <div data-testid="my-bookings-results" className="mt-6 space-y-3">
          {bookings.length === 0 && (
            <p data-testid="no-bookings-message" className="rounded-xl border border-zinc-800 bg-[#161618] p-4 text-center text-sm text-zinc-400">
              Nenhum agendamento encontrado para este telefone.
            </p>
          )}
          {bookings.map((b) => (
            <div
              key={b.id}
              data-testid={`booking-card-${b.id}`}
              className={`rounded-xl border p-4 ${
                b.status === "cancelado"
                  ? "border-zinc-800/60 bg-zinc-900/40 opacity-60"
                  : "border-amber-500/15 bg-[#161618]"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-zinc-100">
                    {(b.service_names?.length ? b.service_names : [b.service_name])
                      .filter(Boolean)
                      .join(" + ")}
                  </p>
                  <p className="mt-1 flex items-center gap-3 text-xs text-zinc-400">
                    <span className="flex items-center gap-1 capitalize">
                      <CalendarDays className="h-3.5 w-3.5 text-amber-400" /> {fmt(b.date)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5 text-amber-400" /> {b.time}
                    </span>
                    {b.barber_name && <span>· com {b.barber_name}</span>}
                  </p>
                  <p className="mt-1.5 text-xs">
                    <span
                      className={`font-semibold ${
                        b.status === "cancelado" ? "text-red-400" : "text-emerald-400"
                      }`}
                    >
                      {b.status === "cancelado" ? "Cancelado" : "Confirmado"}
                    </span>
                    <span className="text-zinc-500"> · {BRL(b.price)} · </span>
                    <span className="text-zinc-500">
                      {b.payment_status === "pago"
                        ? "Pago via PicPay"
                        : b.payment_method === "picpay"
                          ? "Pagamento pendente"
                          : "Pagar no local"}
                    </span>
                  </p>
                </div>
                {b.status === "confirmado" && (
                  <button
                    data-testid={`cancel-booking-${b.id}`}
                    onClick={() => cancel(b.id)}
                    disabled={cancelling === b.id}
                    className="flex items-center gap-1 rounded-full border border-red-500/40 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-red-400 transition-colors hover:bg-red-500/10 disabled:opacity-40"
                  >
                    {cancelling === b.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <XCircle className="h-3 w-3" />
                    )}
                    Cancelar
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
